import React from 'react';

/**
 * 🪵 木魚 SVG 圖標組件
 * 支援多種類型的木魚外觀
 */
const MokugyoIcon = ({ type = 'normal', size = 32, className = '' }) => {
  const renderNormalFish = () => (
    <>
      {/* 漸層定義 */}
      <defs>
        <linearGradient id="fishGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#f9b294', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#d97706', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient id="highlightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#ffecd1', stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: '#f9b294', stopOpacity: 0.3 }} />
        </linearGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#fishGradient)"
        stroke="#2d1b10"
        strokeWidth="2.5"
      />

      {/* 高光效果 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#highlightGradient)" opacity="0.6" />

      {/* 👁️ 左眼 - 同步眨眼 */}
      <g className="fish-eye">
        <ellipse cx="22" cy="30" rx="4" ry="5" fill="white" stroke="#2d1b10" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2.5" ry="3.5" fill="#2d1b10" />
      </g>

      {/* 👁️ 右眼 - 同步眨眼 */}
      <g className="fish-eye">
        <ellipse cx="42" cy="30" rx="4" ry="5" fill="white" stroke="#2d1b10" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2.5" ry="3.5" fill="#2d1b10" />
      </g>

      {/* 😊 微笑嘴巴 (向上弧度) */}
      <path
        d="M 20 44 Q 32 48 44 44"
        fill="none"
        stroke="#2d1b10"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* 木魚紋路 */}
      <path
        d="M 12 40 Q 32 42 52 40"
        fill="none"
        stroke="#8a6d5d"
        strokeWidth="1.5"
        opacity="0.5"
      />
      <path
        d="M 14 48 Q 32 50 50 48"
        fill="none"
        stroke="#8a6d5d"
        strokeWidth="1.5"
        opacity="0.5"
      />

      {/* 👁️ CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes fishBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .fish-eye {
            animation: fishBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const renderGoldenFish = () => (
    <>
      {/* 黃金漸層 */}
      <defs>
        <linearGradient id="goldenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#ffd700', stopOpacity: 1 }} />
          <stop offset="50%" style={{ stopColor: '#ffed4e', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#f9b662', stopOpacity: 1 }} />
        </linearGradient>
        <radialGradient id="goldenShine" cx="30%" cy="30%">
          <stop offset="0%" style={{ stopColor: '#fffacd', stopOpacity: 0.9 }} />
          <stop offset="100%" style={{ stopColor: '#ffd700', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#goldenGradient)"
        stroke="#8b6914"
        strokeWidth="2.5"
      />

      {/* 閃光效果 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#goldenShine)" opacity="0.7" />

      {/* 👁️ 左眼 - 同步眨眼 */}
      <g className="golden-eye">
        <ellipse cx="22" cy="30" rx="4" ry="5" fill="white" stroke="#8b6914" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2.5" ry="3.5" fill="#8b6914" />
      </g>

      {/* 👁️ 右眼 - 同步眨眼 */}
      <g className="golden-eye">
        <ellipse cx="42" cy="30" rx="4" ry="5" fill="white" stroke="#8b6914" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2.5" ry="3.5" fill="#8b6914" />
      </g>

      {/* 😊 微笑嘴巴 (向上弧度) */}
      <path
        d="M 20 44 Q 32 48 44 44"
        fill="none"
        stroke="#8b6914"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* 金色紋路 */}
      <path
        d="M 12 40 Q 32 42 52 40"
        fill="none"
        stroke="#daa520"
        strokeWidth="1.5"
        opacity="0.6"
      />
      <path
        d="M 14 48 Q 32 50 50 48"
        fill="none"
        stroke="#daa520"
        strokeWidth="1.5"
        opacity="0.6"
      />

      {/* 閃耀星星 */}
      <g opacity="0.8">
        <path
          d="M 48 20 L 49 22 L 51 22 L 49.5 23.5 L 50 25.5 L 48 24 L 46 25.5 L 46.5 23.5 L 45 22 L 47 22 Z"
          fill="#fffacd"
        />
        <path
          d="M 16 24 L 17 25.5 L 18.5 25.5 L 17.5 26.5 L 18 28 L 16.5 27 L 15 28 L 15.5 26.5 L 14.5 25.5 L 16 25.5 Z"
          fill="#fffacd"
        />
      </g>

      {/* 👁️ CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes goldenBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .golden-eye {
            animation: goldenBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const renderCrystalFish = () => (
    <>
      {/* 水晶漸層 */}
      <defs>
        <linearGradient id="crystalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#e0f7ff', stopOpacity: 0.9 }} />
          <stop offset="50%" style={{ stopColor: '#a8e6ff', stopOpacity: 0.95 }} />
          <stop offset="100%" style={{ stopColor: '#d4f2ff', stopOpacity: 0.9 }} />
        </linearGradient>
        <radialGradient id="crystalShine" cx="30%" cy="30%">
          <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: '#a8e6ff', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#crystalGradient)"
        stroke="#5b9aa8"
        strokeWidth="2.5"
        opacity="0.95"
      />

      {/* 水晶光澤 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#crystalShine)" opacity="0.8" />
      <ellipse cx="38" cy="32" rx="8" ry="6" fill="url(#crystalShine)" opacity="0.5" />

      {/* 👁️ 左眼 - 同步眨眼 */}
      <g className="crystal-eye">
        <ellipse cx="22" cy="30" rx="4" ry="5" fill="white" stroke="#5b9aa8" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2.5" ry="3.5" fill="#5b9aa8" />
      </g>

      {/* 👁️ 右眼 - 同步眨眼 */}
      <g className="crystal-eye">
        <ellipse cx="42" cy="30" rx="4" ry="5" fill="white" stroke="#5b9aa8" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2.5" ry="3.5" fill="#5b9aa8" />
      </g>

      {/* 😊 微笑嘴巴 (向上弧度) */}
      <path
        d="M 20 44 Q 32 48 44 44"
        fill="none"
        stroke="#5b9aa8"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* 水晶紋路 */}
      <path
        d="M 12 40 Q 32 42 52 40"
        fill="none"
        stroke="#87ceeb"
        strokeWidth="1.5"
        opacity="0.6"
      />
      <path
        d="M 14 48 Q 32 50 50 48"
        fill="none"
        stroke="#87ceeb"
        strokeWidth="1.5"
        opacity="0.6"
      />

      {/* 水滴裝飾 */}
      <g opacity="0.7">
        <ellipse cx="16" cy="26" rx="2" ry="3" fill="#a8e6ff" />
        <ellipse cx="48" cy="24" rx="2" ry="3" fill="#a8e6ff" />
        <ellipse cx="32" cy="18" rx="2" ry="3" fill="#a8e6ff" />
      </g>

      {/* 👁️ CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes crystalBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .crystal-eye {
            animation: crystalBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const renderJadeFish = () => (
    <>
      {/* 翡翠漸層 */}
      <defs>
        <linearGradient id="jadeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#b5e8c3', stopOpacity: 1 }} />
          <stop offset="50%" style={{ stopColor: '#7ecea0', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#a8d5ba', stopOpacity: 1 }} />
        </linearGradient>
        <radialGradient id="jadeShine" cx="30%" cy="30%">
          <stop offset="0%" style={{ stopColor: '#e8f5e9', stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: '#7ecea0', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#jadeGradient)"
        stroke="#2e7d32"
        strokeWidth="2.5"
      />

      {/* 玉石光澤 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#jadeShine)" opacity="0.7" />

      {/* 👁️ 左眼 - 同步眨眼 */}
      <g className="jade-eye">
        <ellipse cx="22" cy="30" rx="4" ry="5" fill="white" stroke="#2e7d32" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2.5" ry="3.5" fill="#2e7d32" />
      </g>

      {/* 👁️ 右眼 - 同步眨眼 */}
      <g className="jade-eye">
        <ellipse cx="42" cy="30" rx="4" ry="5" fill="white" stroke="#2e7d32" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2.5" ry="3.5" fill="#2e7d32" />
      </g>

      {/* 😊 微笑嘴巴 (向上弧度) */}
      <path
        d="M 20 44 Q 32 48 44 44"
        fill="none"
        stroke="#2e7d32"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* 翡翠紋路 */}
      <path
        d="M 12 40 Q 32 42 52 40"
        fill="none"
        stroke="#4caf50"
        strokeWidth="1.5"
        opacity="0.6"
      />
      <path
        d="M 14 48 Q 32 50 50 48"
        fill="none"
        stroke="#4caf50"
        strokeWidth="1.5"
        opacity="0.6"
      />

      {/* 葉子裝飾 */}
      <g opacity="0.6">
        <path d="M 14 22 Q 16 20 18 22 Q 16 24 14 22 Z" fill="#81c784" />
        <path d="M 46 20 Q 48 18 50 20 Q 48 22 46 20 Z" fill="#81c784" />
      </g>

      {/* 👁️ CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes jadeBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .jade-eye {
            animation: jadeBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const renderRubyFish = () => (
    <>
      {/* 紅寶石漸層 */}
      <defs>
        <linearGradient id="rubyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#ffcdd2', stopOpacity: 1 }} />
          <stop offset="50%" style={{ stopColor: '#e57373', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#ef5350', stopOpacity: 1 }} />
        </linearGradient>
        <radialGradient id="rubyShine" cx="30%" cy="30%">
          <stop offset="0%" style={{ stopColor: '#ffebee', stopOpacity: 0.9 }} />
          <stop offset="100%" style={{ stopColor: '#e57373', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#rubyGradient)"
        stroke="#b71c1c"
        strokeWidth="2.5"
      />

      {/* 寶石光澤 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#rubyShine)" opacity="0.7" />

      {/* 👁️ 左眼 - 同步眨眼 */}
      <g className="ruby-eye">
        <ellipse cx="22" cy="30" rx="4" ry="5" fill="white" stroke="#b71c1c" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2.5" ry="3.5" fill="#b71c1c" />
      </g>

      {/* 👁️ 右眼 - 同步眨眼 */}
      <g className="ruby-eye">
        <ellipse cx="42" cy="30" rx="4" ry="5" fill="white" stroke="#b71c1c" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2.5" ry="3.5" fill="#b71c1c" />
      </g>

      {/* 😊 微笑嘴巴 (向上弧度) */}
      <path
        d="M 20 44 Q 32 48 44 44"
        fill="none"
        stroke="#b71c1c"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* 紅寶石紋路 */}
      <path
        d="M 12 40 Q 32 42 52 40"
        fill="none"
        stroke="#f44336"
        strokeWidth="1.5"
        opacity="0.6"
      />
      <path
        d="M 14 48 Q 32 50 50 48"
        fill="none"
        stroke="#f44336"
        strokeWidth="1.5"
        opacity="0.6"
      />

      {/* 愛心裝飾 */}
      <g opacity="0.7">
        <path
          d="M 16 22 C 16 20 14 18 12 20 C 10 22 12 24 16 26 C 20 24 22 22 20 20 C 18 18 16 20 16 22 Z"
          fill="#ff8a80"
          transform="scale(0.5) translate(16, 24)"
        />
        <path
          d="M 48 20 C 48 18 46 16 44 18 C 42 20 44 22 48 24 C 52 22 54 20 52 18 C 50 16 48 18 48 20 Z"
          fill="#ff8a80"
          transform="scale(0.4) translate(48, 28)"
        />
      </g>

      {/* 👁️ CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes rubyBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .ruby-eye {
            animation: rubyBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const renderCatFish = () => (
    <>
      {/* 貓咪漸層 */}
      <defs>
        <linearGradient id="catGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#ffdfd3', stopOpacity: 1 }} />
          <stop offset="50%" style={{ stopColor: '#ffcbb3', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#ffb499', stopOpacity: 1 }} />
        </linearGradient>
        <radialGradient id="catShine" cx="30%" cy="30%">
          <stop offset="0%" style={{ stopColor: '#fff5f0', stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: '#ffcbb3', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#catGradient)"
        stroke="#8d6e63"
        strokeWidth="2.5"
      />

      {/* 柔和光澤 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#catShine)" opacity="0.6" />

      {/* 貓耳朵 */}
      <g opacity="0.8">
        <path d="M 12 18 L 8 8 L 18 14 Z" fill="#ffb499" stroke="#8d6e63" strokeWidth="1.5" />
        <path d="M 52 18 L 56 8 L 46 14 Z" fill="#ffb499" stroke="#8d6e63" strokeWidth="1.5" />
        {/* 耳朵內側 */}
        <path d="M 12 16 L 10 10 L 16 14 Z" fill="#ffc4b3" />
        <path d="M 52 16 L 54 10 L 48 14 Z" fill="#ffc4b3" />
      </g>

      {/* 🐱 左眼 (貓眼) - 同步眨眼 */}
      <g className="cat-eye">
        <ellipse cx="22" cy="30" rx="5" ry="6" fill="white" stroke="#8d6e63" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2" ry="4" fill="#4a4a4a" />
      </g>

      {/* 🐱 右眼 (貓眼) - 同步眨眼 */}
      <g className="cat-eye">
        <ellipse cx="42" cy="30" rx="5" ry="6" fill="white" stroke="#8d6e63" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2" ry="4" fill="#4a4a4a" />
      </g>

      {/* 貓鼻子 */}
      <path d="M 32 38 L 30 42 L 34 42 Z" fill="#ff6b9d" />

      {/* 😸 貓咪微笑嘴巴 (W 型向上微笑) */}
      <path d="M 32 42 L 32 44" stroke="#8d6e63" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M 32 44 Q 28 46 24 44"
        fill="none"
        stroke="#8d6e63"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M 32 44 Q 36 46 40 44"
        fill="none"
        stroke="#8d6e63"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* 貓鬍鬚 */}
      <g opacity="0.6">
        <line x1="12" y1="36" x2="4" y2="34" stroke="#8d6e63" strokeWidth="1" />
        <line x1="12" y1="38" x2="4" y2="38" stroke="#8d6e63" strokeWidth="1" />
        <line x1="12" y1="40" x2="4" y2="42" stroke="#8d6e63" strokeWidth="1" />
        <line x1="52" y1="36" x2="60" y2="34" stroke="#8d6e63" strokeWidth="1" />
        <line x1="52" y1="38" x2="60" y2="38" stroke="#8d6e63" strokeWidth="1" />
        <line x1="52" y1="40" x2="60" y2="42" stroke="#8d6e63" strokeWidth="1" />
      </g>

      {/* 貓掌印 */}
      <g opacity="0.5">
        <ellipse cx="18" cy="50" rx="3" ry="2" fill="#ffb499" />
        <ellipse cx="46" cy="50" rx="3" ry="2" fill="#ffb499" />
      </g>

      {/* 🐱 CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes catBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .cat-eye {
            animation: catBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const renderRainbowFish = () => (
    <>
      {/* 彩虹漸層 */}
      <defs>
        <linearGradient id="rainbowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#ff5757', stopOpacity: 1 }} />
          <stop offset="20%" style={{ stopColor: '#ffbd59', stopOpacity: 1 }} />
          <stop offset="40%" style={{ stopColor: '#88e079', stopOpacity: 1 }} />
          <stop offset="60%" style={{ stopColor: '#5ae7ff', stopOpacity: 1 }} />
          <stop offset="80%" style={{ stopColor: '#7c84ff', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#cf6fea', stopOpacity: 1 }} />
        </linearGradient>
        <radialGradient id="rainbowShine" cx="30%" cy="30%">
          <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: '#ff5757', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#rainbowGradient)"
        stroke="#6b46c1"
        strokeWidth="2.5"
      />

      {/* 彩虹光澤 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#rainbowShine)" opacity="0.7" />

      {/* 👁️ 左眼 - 同步眨眼 */}
      <g className="rainbow-eye">
        <ellipse cx="22" cy="30" rx="4" ry="5" fill="white" stroke="#6b46c1" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2.5" ry="3.5" fill="#6b46c1" />
      </g>

      {/* 👁️ 右眼 - 同步眨眼 */}
      <g className="rainbow-eye">
        <ellipse cx="42" cy="30" rx="4" ry="5" fill="white" stroke="#6b46c1" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2.5" ry="3.5" fill="#6b46c1" />
      </g>

      {/* 😊 微笑嘴巴 (向上弧度) */}
      <path
        d="M 20 44 Q 32 48 44 44"
        fill="none"
        stroke="#6b46c1"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* 彩虹波紋 */}
      <path
        d="M 12 40 Q 32 42 52 40"
        fill="none"
        stroke="url(#rainbowGradient)"
        strokeWidth="1.5"
        opacity="0.7"
      />
      <path
        d="M 14 48 Q 32 50 50 48"
        fill="none"
        stroke="url(#rainbowGradient)"
        strokeWidth="1.5"
        opacity="0.7"
      />

      {/* 星星裝飾 */}
      <g opacity="0.8">
        <path
          d="M 14 20 L 15 22 L 17 22 L 15.5 23.5 L 16 25.5 L 14 24 L 12 25.5 L 12.5 23.5 L 11 22 L 13 22 Z"
          fill="#ffe066"
        />
        <path
          d="M 50 18 L 51 20 L 53 20 L 51.5 21.5 L 52 23.5 L 50 22 L 48 23.5 L 48.5 21.5 L 47 20 L 49 20 Z"
          fill="#ffe066"
        />
        <path
          d="M 32 14 L 33 16 L 35 16 L 33.5 17.5 L 34 19.5 L 32 18 L 30 19.5 L 30.5 17.5 L 29 16 L 31 16 Z"
          fill="#ffe066"
        />
      </g>

      {/* 👁️ CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes rainbowBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .rainbow-eye {
            animation: rainbowBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const renderCosmicFish = () => (
    <>
      {/* 宇宙漸層 */}
      <defs>
        <linearGradient id="cosmicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#673ab7', stopOpacity: 1 }} />
          <stop offset="33%" style={{ stopColor: '#3f51b5', stopOpacity: 1 }} />
          <stop offset="66%" style={{ stopColor: '#2196f3', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#00bcd4', stopOpacity: 1 }} />
        </linearGradient>
        <radialGradient id="cosmicShine" cx="30%" cy="30%">
          <stop offset="0%" style={{ stopColor: '#e1bee7', stopOpacity: 0.9 }} />
          <stop offset="100%" style={{ stopColor: '#673ab7', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* 木魚外殼 */}
      <ellipse
        cx="32"
        cy="36"
        rx="26"
        ry="24"
        fill="url(#cosmicGradient)"
        stroke="#311b92"
        strokeWidth="2.5"
      />

      {/* 宇宙光澤 */}
      <ellipse cx="24" cy="28" rx="12" ry="10" fill="url(#cosmicShine)" opacity="0.7" />
      <ellipse cx="40" cy="34" rx="8" ry="6" fill="url(#cosmicShine)" opacity="0.4" />

      {/* 👁️ 左眼 - 同步眨眼 */}
      <g className="cosmic-eye">
        <ellipse cx="22" cy="30" rx="4" ry="5" fill="white" stroke="#311b92" strokeWidth="1.5" />
        <ellipse cx="22" cy="31" rx="2.5" ry="3.5" fill="#311b92" />
      </g>

      {/* 👁️ 右眼 - 同步眨眼 */}
      <g className="cosmic-eye">
        <ellipse cx="42" cy="30" rx="4" ry="5" fill="white" stroke="#311b92" strokeWidth="1.5" />
        <ellipse cx="42" cy="31" rx="2.5" ry="3.5" fill="#311b92" />
      </g>

      {/* 😊 微笑嘴巴 (向上弧度) */}
      <path
        d="M 20 44 Q 32 48 44 44"
        fill="none"
        stroke="#311b92"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* 宇宙紋路 */}
      <path
        d="M 12 40 Q 32 42 52 40"
        fill="none"
        stroke="#7c4dff"
        strokeWidth="1.5"
        opacity="0.6"
      />
      <path
        d="M 14 48 Q 32 50 50 48"
        fill="none"
        stroke="#7c4dff"
        strokeWidth="1.5"
        opacity="0.6"
      />

      {/* 星系裝飾 */}
      <g opacity="0.8">
        {/* 小星星 */}
        <circle cx="14" cy="22" r="1.5" fill="#e1bee7" />
        <circle cx="18" cy="18" r="1" fill="#ce93d8" />
        <circle cx="48" cy="20" r="1.5" fill="#e1bee7" />
        <circle cx="52" cy="26" r="1" fill="#ce93d8" />
        <circle cx="32" cy="16" r="1.2" fill="#e1bee7" />
        <circle cx="36" cy="20" r="0.8" fill="#ce93d8" />

        {/* 星雲效果 */}
        <ellipse cx="20" cy="24" rx="3" ry="2" fill="#7c4dff" opacity="0.3" />
        <ellipse cx="44" cy="22" rx="2.5" ry="2" fill="#7c4dff" opacity="0.3" />
      </g>

      {/* 👁️ CSS 動畫定義 - 同步眨眼 */}
      <style>
        {`
          @keyframes cosmicBlink {
            0%, 90%, 100% { transform: scaleY(1); }
            95% { transform: scaleY(0.1); }
          }
          .cosmic-eye {
            animation: cosmicBlink 4s ease-in-out infinite;
            transform-origin: center;
          }
        `}
      </style>
    </>
  );

  const types = {
    normal: renderNormalFish,
    golden: renderGoldenFish,
    crystal: renderCrystalFish,
    jade: renderJadeFish,
    ruby: renderRubyFish,
    cat: renderCatFish,
    rainbow: renderRainbowFish,
    cosmic: renderCosmicFish
  };

  const renderContent = types[type] || renderNormalFish;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      {renderContent()}
    </svg>
  );
};

export default MokugyoIcon;